import {
  SIDESHEET_COOKIE_NAMES,
  SIDESHEET_COOKIE_MAX_AGE_24H,
} from '@integration-layer/composables/useSidesheetVisibility'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import { isProductRoute } from '@integration-layer/utils/url'

/**
 * Middleware to handle newsletter sidebar display logic (centralized sidesheet visibility).
 *
 * Shows the newsletter sidebar only when:
 * 1. Country is shoppable (isShoppable = true)
 * 2. User is on at least the 4th non-PDP page view (pageCount starts at 1 and
 *    increments on every non-PDP path change, so this is after 3 navigations)
 * 3. User has neither abandoned-cart nor last-viewed-sidesheet cookie set (they didn't see those sidesheets)
 * 4. User is not logged in (loggati non devono mai visualizzare il componente)
 * 5. No hideNL query param: hideNL=<days> inhibits for x days (country-independent links); hideNL=true inhibits for 24h
 *
 * Cookie: newsletter visible at most once per day (24h).
 */
export default defineNuxtRouteMiddleware((to, from) => {
  const { loggedIn: isLogged } = useUserSession()

  if (isLogged.value) return

  // ── Country must be shoppable ────────────────────────────────────────────
  const { isShoppable } = useIsShoppable()
  if (!isShoppable.value) return

  const hideNLParam = to.query.hideNL
  const newsletterHideCookie =
    hideNLParam !== undefined && hideNLParam !== null
      ? (() => {
          const days =
            typeof hideNLParam === 'string' && /^\d+$/.test(hideNLParam)
              ? Math.min(365, Math.max(1, parseInt(hideNLParam, 10)))
              : 1
          const cookie = useCookie(SIDESHEET_COOKIE_NAMES.newsletterHideDays, {
            maxAge: days * 24 * 60 * 60,
          })
          cookie.value = 'true'
          return cookie
        })()
      : useCookie(SIDESHEET_COOKIE_NAMES.newsletterHideDays)

  if (newsletterHideCookie.value) return

  const abandonedCartCookie = useCookie(SIDESHEET_COOKIE_NAMES.abandonedCart)
  const lastViewedCookie = useCookie(SIDESHEET_COOKIE_NAMES.lastViewed)
  if (abandonedCartCookie.value || lastViewedCookie.value) return

  const newsletterClosedCookie = useCookie(SIDESHEET_COOKIE_NAMES.newsletter, {
    maxAge: SIDESHEET_COOKIE_MAX_AGE_24H,
  })
  if (newsletterClosedCookie.value) return

  const pageCount = useSessionStorage('pageCount', 1)

  const isPdp = isProductRoute(to)
  if (to.path !== from.path && !isPdp) pageCount.value++

  if (pageCount.value < 4) return

  const { menuStatus: asideMenuStatus } = useMenu()

  const skipSidebarShown = asideMenuStatus.value.isActive

  if (!skipSidebarShown) {
    const { openDialog } = useDialog(SIDEBAR_IDS.newsletterSidebar)
    openDialog(SIDEBAR_IDS.newsletterSidebar)
  }
})
